import { useEffect, useState } from 'react';
import { Droplets, Landmark, Percent, PieChart, TrendingDown } from 'lucide-react';
import { formatUnits, parseUnits } from 'viem';
import type { Address } from 'viem';
import { useAccount, useReadContract, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { ERC20_ABI, LENDING_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment } from '../web3/contracts';
import { logTransaction, type TxType } from '../web3/logTransaction';
import TokenIcon from './TokenIcon';
import TrustBar from './TrustBar';
import TransactionTimeout from './TransactionTimeout';

function fmt(value: bigint | undefined, digits = 4) {
  if (value === undefined) return '-';
  return Number(formatUnits(value, 18)).toLocaleString('en-US', { maximumFractionDigits: digits });
}

function safeParse(value: string): bigint | null {
  if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) return null;
  try {
    return parseUnits(value as `${number}`, 18);
  } catch {
    return null;
  }
}

export default function LendingView() {
  const { address, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const lending = deployment?.lending;

  const { data: liquidity } = useReadContract({
    address: lending?.address,
    abi: LENDING_ABI,
    functionName: 'availableLiquidity',
    chainId: readChainId,
    query: { enabled: Boolean(lending), refetchInterval: 15000 },
  });

  const { data: borrowApr } = useReadContract({
    address: lending?.address,
    abi: LENDING_ABI,
    functionName: 'borrowAprBps',
    chainId: readChainId,
    query: { enabled: Boolean(lending) },
  });

  const { data: totalDebtShares } = useReadContract({
    address: lending?.address,
    abi: LENDING_ABI,
    functionName: 'totalDebtShares',
    chainId: readChainId,
    query: { enabled: Boolean(lending), refetchInterval: 15000 },
  });

  const { data: borrowIndex } = useReadContract({
    address: lending?.address,
    abi: LENDING_ABI,
    functionName: 'currentIndex',
    chainId: readChainId,
    query: { enabled: Boolean(lending), refetchInterval: 15000 },
  });

  const { data: debt, refetch: refetchDebt } = useReadContract({
    address: lending?.address,
    abi: LENDING_ABI,
    functionName: 'debtOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(lending && address), refetchInterval: 10000 },
  });

  const { data: borrowLimit, refetch: refetchLimit } = useReadContract({
    address: lending?.address,
    abi: LENDING_ABI,
    functionName: 'borrowLimitOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(lending && address), refetchInterval: 10000 },
  });

  const { data: liqLimit, refetch: refetchLiqLimit } = useReadContract({
    address: lending?.address,
    abi: LENDING_ABI,
    functionName: 'liquidationLimitOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(lending && address), refetchInterval: 10000 },
  });

  const { data: collateralValue, refetch: refetchCollateralValue } = useReadContract({
    address: lending?.address,
    abi: LENDING_ABI,
    functionName: 'collateralValueOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(lending && address), refetchInterval: 10000 },
  });

  if (!lending) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-foam-500">
        Lending is not deployed on this network.
      </div>
    );
  }

  const debtNum = debt !== undefined ? Number(formatUnits(debt, 18)) : 0;
  const liqNum = liqLimit !== undefined ? Number(formatUnits(liqLimit, 18)) : 0;
  const healthFactor = debt === undefined || liqLimit === undefined
    ? undefined
    : debtNum > 0
      ? liqNum / debtNum
      : Number.POSITIVE_INFINITY;
  const healthStatus = healthFactor === undefined
    ? { label: '확인 중', text: 'text-foam-400', badge: 'bg-abyss-700' }
    : healthFactor > 1.5
      ? { label: '안전', text: 'text-tide-400', badge: 'bg-tide-400/15' }
      : healthFactor >= 1.1
        ? { label: '주의', text: 'text-amber-300', badge: 'bg-amber-400/15' }
        : { label: '청산 위험', text: 'text-coral-400', badge: 'bg-coral-500/15' };
  const healthGaugePosition = healthFactor === undefined
    ? undefined
    : Number.isFinite(healthFactor)
      ? Math.min(Math.max((healthFactor / 2) * 100, 0), 100)
      : 100;

  const availNum = liquidity !== undefined ? Number(formatUnits(liquidity, 18)) : 0;
  const borrowedNum =
    totalDebtShares !== undefined && borrowIndex !== undefined
      ? (Number(formatUnits(totalDebtShares, 18)) * Number(formatUnits(borrowIndex, 18)))
      : 0;
  const utilization = availNum + borrowedNum > 0 ? (borrowedNum / (availNum + borrowedNum)) * 100 : 0;
  const aprPct = borrowApr !== undefined ? Number(borrowApr) / 100 : undefined;

  const refetchAccount = () => {
    refetchDebt();
    refetchLimit();
    refetchLiqLimit();
    refetchCollateralValue();
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
          <Landmark size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foam-100">Lending</h2>
          <p className="text-xs text-foam-500 mt-0.5">
            BTCB·ETH를 담보로 USDT를 빌리는 담보대출 — 고정 이자 {aprPct !== undefined ? `${aprPct.toFixed(0)}%` : '10%'} APR, LTV 60%
          </p>
        </div>
      </div>

      <div className="sticky top-0 z-20 rounded-xl border border-abyss-700 bg-abyss-900/95 p-4 shadow-lg backdrop-blur">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs font-semibold text-foam-300">Health Factor</div>
            <div className="text-[11px] text-foam-500 mt-0.5">온체인 담보의 오라클 평가액과 청산 한도 기준</div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-2xl font-semibold tabular-nums ${healthStatus.text}`}>
              {healthFactor === undefined ? '—' : Number.isFinite(healthFactor) ? healthFactor.toFixed(2) : '∞'}
            </span>
            <span className={`rounded-md px-2 py-1 text-xs font-semibold ${healthStatus.text} ${healthStatus.badge}`}>
              {healthStatus.label}
            </span>
          </div>
        </div>
        <div className="relative mt-3 h-2 overflow-visible rounded-full bg-abyss-800">
          <div className="flex h-full overflow-hidden rounded-full">
            <div className="w-[55%] bg-coral-500/70" />
            <div className="w-[20%] bg-amber-400/70" />
            <div className="flex-1 bg-tide-500/70" />
          </div>
          {healthGaugePosition !== undefined && (
            <div
              className="absolute top-1/2 h-4 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow"
              style={{ left: `${healthGaugePosition}%` }}
            />
          )}
        </div>
        <div className="mt-1.5 flex justify-between text-[10px] text-foam-600 tabular-nums">
          <span>0</span>
          <span>1.1 청산 위험</span>
          <span>1.5 안전</span>
          <span>2.0+</span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MarketStat
          icon={Droplets}
          label="예치 유동성"
          value={`${(availNum + borrowedNum).toLocaleString('en-US', { maximumFractionDigits: 2 })} USDT`}
        />
        <MarketStat
          icon={TrendingDown}
          label="총 대출"
          value={`${borrowedNum.toLocaleString('en-US', { maximumFractionDigits: 2 })} USDT`}
        />
        <MarketStat icon={Percent} label="대출 이자율" value={aprPct !== undefined ? `${aprPct.toFixed(1)}%` : '-'} accent />
        <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
          <div className="flex items-center gap-1.5 text-[11px] text-foam-500">
            <PieChart size={12} />
            자금 이용률
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-abyss-900 overflow-hidden">
            <div className="h-full bg-tide-500 transition-all duration-500" style={{ width: `${utilization}%` }} />
          </div>
          <div className="text-sm font-semibold text-foam-100 mt-1.5 tabular-nums">{utilization.toFixed(1)}%</div>
        </div>
      </div>

      <div className="text-xs font-semibold text-foam-400 -mb-2">내 포지션</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="내 담보 가치" value={`$${fmt(collateralValue, 2)}`} />
        <Stat label="대출 한도 (LTV 60%)" value={`$${fmt(borrowLimit, 2)}`} />
        <Stat label="현재 부채" value={`${fmt(debt, 4)} USDT`} accent={debtNum > 0} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foam-100">담보 관리</h3>
          {lending.collaterals.map((symbol) => (
            <CollateralCard
              key={symbol}
              symbol={symbol}
              lendingAddress={lending.address}
              onChanged={refetchAccount}
            />
          ))}
        </div>
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foam-100">USDT 대출 / 상환</h3>
          <BorrowPanel lendingAddress={lending.address} onChanged={refetchAccount} />
        </div>
      </div>

      <TrustBar
        contract={lending.address}
        badges={['nonCustodial', 'onchain']}
        note="담보 가격은 온체인 Chainlink 오라클을 사용합니다. 오라클이 1시간 이상 갱신되지 않으면 가격 의존 호출이 거부됩니다. Health Factor가 1 미만이면 누구나 청산(부채 대납 후 설정된 담보 보너스)을 실행할 수 있습니다."
      />
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
      <div className="text-xs text-foam-500">{label}</div>
      <div className={`text-xl font-semibold mt-1.5 tabular-nums ${accent ? 'text-coral-400' : 'text-foam-100'}`}>
        {value}
      </div>
    </div>
  );
}

function MarketStat({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Droplets;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
      <div className="flex items-center gap-1.5 text-[11px] text-foam-500">
        <Icon size={12} />
        {label}
      </div>
      <div className={`text-base font-semibold mt-1.5 tabular-nums truncate ${accent ? 'text-tide-400' : 'text-foam-100'}`}>
        {value}
      </div>
    </div>
  );
}

function CollateralCard({
  symbol,
  lendingAddress,
  onChanged,
}: {
  symbol: string;
  lendingAddress: Address;
  onChanged: () => void;
}) {
  const { address, isConnected, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const tokenAddress = deployment?.tokens[symbol];

  const [mode, setMode] = useState<'deposit' | 'withdraw'>('deposit');
  const [amount, setAmount] = useState('');
  const [txError, setTxError] = useState<string | null>(null);
  const amountWei = safeParse(amount);

  const { data: tokenPrice } = useReadContract({
    address: lendingAddress,
    abi: LENDING_ABI,
    functionName: 'price',
    args: tokenAddress ? [tokenAddress] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(tokenAddress), refetchInterval: 15000 },
  });

  const { data: deposited, refetch: refetchDeposited } = useReadContract({
    address: lendingAddress,
    abi: LENDING_ABI,
    functionName: 'collateral',
    args: address && tokenAddress ? [address, tokenAddress] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(address && tokenAddress) },
  });

  const { data: walletBalance, refetch: refetchWallet } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(tokenAddress && address) },
  });

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: tokenAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address ? [address, lendingAddress] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(tokenAddress && address) },
  });

  const needsApproval =
    mode === 'deposit' && amountWei !== null && (allowance === undefined || allowance < amountWei);

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed, error: approveReceiptError, refetch: retryApproveReceipt } = useWaitForTransactionReceipt({
    hash: approveHash,
    timeout: 300_000,
  });

  const { writeContract, data: hash, isPending, reset } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed, error: receiptError, refetch: retryReceipt } = useWaitForTransactionReceipt({ hash, timeout: 300_000 });

  useEffect(() => {
    if (isApproveConfirmed) refetchAllowance();
  }, [isApproveConfirmed, refetchAllowance]);

  useEffect(() => {
    if (isConfirmed && hash && address) {
      logTransaction({
        type: (mode === 'deposit' ? 'lendDeposit' : 'lendWithdraw') as TxType,
        hash,
        account: address,
        chainId: readChainId,
        details: { pool: `LEND-${symbol}`, amount },
      });
      setAmount('');
      refetchDeposited();
      refetchWallet();
      onChanged();
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmed]);

  const handleApprove = () => {
    if (!tokenAddress) return;
    setTxError(null);
    writeApprove(
      { address: tokenAddress, abi: ERC20_ABI, functionName: 'approve', args: [lendingAddress, 2n ** 256n - 1n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleAction = () => {
    if (!amountWei || !tokenAddress) return;
    setTxError(null);
    writeContract(
      {
        address: lendingAddress,
        abi: LENDING_ABI,
        functionName: mode === 'deposit' ? 'depositCollateral' : 'withdrawCollateral',
        args: [tokenAddress, amountWei],
      },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const busy = isApprovePending || isApproveConfirming || isPending || isConfirming;
  const maxAmount = mode === 'deposit' ? walletBalance : deposited;

  let buttonLabel = '수량을 입력하세요';
  let buttonAction: (() => void) | undefined;
  let buttonDisabled = true;

  if (!isConnected) {
    buttonLabel = '지갑을 연결하세요';
  } else if (!onCorrectChain) {
    buttonLabel = '네트워크를 전환하세요';
  } else if (amountWei) {
    if (needsApproval) {
      buttonLabel = `Approve ${symbol}`;
      buttonAction = handleApprove;
      buttonDisabled = busy;
    } else {
      buttonLabel = mode === 'deposit' ? '담보 예치' : '담보 출금';
      buttonAction = handleAction;
      buttonDisabled = busy;
    }
  }

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TokenIcon symbol={symbol} size={26} />
          <div>
            <div className="text-sm font-semibold text-foam-100">{symbol}</div>
            <div className="text-[11px] text-foam-500 tabular-nums">
              ${tokenPrice !== undefined ? fmt(tokenPrice, 2) : '-'} (풀 스팟)
            </div>
          </div>
        </div>
        <div className="text-right text-[11px] text-foam-500">
          예치 중
          <div className="text-foam-100 font-semibold text-xs tabular-nums">{fmt(deposited, 6)}</div>
        </div>
      </div>

      <div className="flex gap-1 p-0.5 rounded-lg bg-abyss-900 border border-abyss-700 w-fit">
        {(['deposit', 'withdraw'] as const).map((m) => (
          <button
            key={m}
            onClick={() => {
              setMode(m);
              setAmount('');
              setTxError(null);
            }}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
              mode === m ? 'bg-tide-500 text-abyss-950' : 'text-foam-300 hover:text-foam-100'
            }`}
          >
            {m === 'deposit' ? '예치' : '출금'}
          </button>
        ))}
      </div>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-900/60">
        <div className="flex justify-between text-[11px] text-foam-500 mb-1.5">
          <span>{mode === 'deposit' ? '지갑 잔액' : '예치 중'}</span>
          <button
            onClick={() => maxAmount && setAmount(formatUnits(maxAmount, 18))}
            className="text-tide-400 hover:text-tide-300 font-medium"
          >
            MAX: {fmt(maxAmount, 6)}
          </button>
        </div>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0.0"
          className="w-full bg-transparent text-lg font-medium text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
        />
      </div>

      <button
        onClick={buttonAction}
        disabled={buttonDisabled || busy}
        className="w-full py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
      >
        {busy ? '처리 중...' : buttonLabel}
      </button>

      {txError && (
        <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>
      )}
      <TransactionTimeout attempts={[
        { error: approveReceiptError, hash: approveHash, retry: retryApproveReceipt },
        { error: receiptError, hash, retry: retryReceipt },
      ]} />
    </div>
  );
}

function BorrowPanel({ lendingAddress, onChanged }: { lendingAddress: Address; onChanged: () => void }) {
  const { address, isConnected, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const usdtAddress = deployment?.tokens.USDT;

  const [mode, setMode] = useState<'borrow' | 'repay'>('borrow');
  const [amount, setAmount] = useState('');
  const [txError, setTxError] = useState<string | null>(null);
  const amountWei = safeParse(amount);

  const { data: usdtBalance, refetch: refetchUsdt } = useReadContract({
    address: usdtAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(usdtAddress && address) },
  });

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: usdtAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address ? [address, lendingAddress] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(usdtAddress && address) },
  });

  const needsApproval = mode === 'repay' && amountWei !== null && (allowance === undefined || allowance < amountWei);

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed, error: approveReceiptError, refetch: retryApproveReceipt } = useWaitForTransactionReceipt({
    hash: approveHash,
    timeout: 300_000,
  });

  const { writeContract, data: hash, isPending, reset } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed, error: receiptError, refetch: retryReceipt } = useWaitForTransactionReceipt({ hash, timeout: 300_000 });

  useEffect(() => {
    if (isApproveConfirmed) refetchAllowance();
  }, [isApproveConfirmed, refetchAllowance]);

  useEffect(() => {
    if (isConfirmed && hash && address) {
      logTransaction({
        type: mode,
        hash,
        account: address,
        chainId: readChainId,
        details: { pool: 'LEND-USDT', amount },
      });
      setAmount('');
      refetchUsdt();
      onChanged();
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmed]);

  const handleApprove = () => {
    if (!usdtAddress) return;
    setTxError(null);
    writeApprove(
      { address: usdtAddress, abi: ERC20_ABI, functionName: 'approve', args: [lendingAddress, 2n ** 256n - 1n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleAction = () => {
    if (!amountWei) return;
    setTxError(null);
    writeContract(
      { address: lendingAddress, abi: LENDING_ABI, functionName: mode, args: [amountWei] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const busy = isApprovePending || isApproveConfirming || isPending || isConfirming;

  let buttonLabel = '수량을 입력하세요';
  let buttonAction: (() => void) | undefined;
  let buttonDisabled = true;

  if (!isConnected) {
    buttonLabel = '지갑을 연결하세요';
  } else if (!onCorrectChain) {
    buttonLabel = '네트워크를 전환하세요';
  } else if (amountWei) {
    if (needsApproval) {
      buttonLabel = 'Approve USDT';
      buttonAction = handleApprove;
      buttonDisabled = busy;
    } else {
      buttonLabel = mode === 'borrow' ? 'USDT 대출' : 'USDT 상환';
      buttonAction = handleAction;
      buttonDisabled = busy;
    }
  }

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TokenIcon symbol="USDT" size={26} />
          <div>
            <div className="text-sm font-semibold text-foam-100">USDT</div>
            <div className="text-[11px] text-foam-500">고정 이자 10% APR</div>
          </div>
        </div>
        <div className="flex gap-1 p-0.5 rounded-lg bg-abyss-900 border border-abyss-700">
          {(['borrow', 'repay'] as const).map((m) => (
            <button
              key={m}
              onClick={() => {
                setMode(m);
                setAmount('');
                setTxError(null);
              }}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                mode === m ? 'bg-tide-500 text-abyss-950' : 'text-foam-300 hover:text-foam-100'
              }`}
            >
              {m === 'borrow' ? '대출' : '상환'}
            </button>
          ))}
        </div>
      </div>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-900/60">
        <div className="flex justify-between text-[11px] text-foam-500 mb-1.5">
          <span>{mode === 'borrow' ? '대출 수량 (USDT)' : '상환 수량 (USDT)'}</span>
          <span>지갑: {fmt(usdtBalance, 2)}</span>
        </div>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0.0"
          className="w-full bg-transparent text-lg font-medium text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
        />
      </div>

      <button
        onClick={buttonAction}
        disabled={buttonDisabled || busy}
        className="w-full py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
      >
        {busy ? '처리 중...' : buttonLabel}
      </button>

      {txError && (
        <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>
      )}
      <TransactionTimeout attempts={[
        { error: approveReceiptError, hash: approveHash, retry: retryApproveReceipt },
        { error: receiptError, hash, retry: retryReceipt },
      ]} />
      {isConfirmed && !txError && (
        <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">
          {mode === 'borrow' ? '대출이 실행되었습니다.' : '상환이 완료되었습니다.'}
        </div>
      )}
    </div>
  );
}
